import {
    type CSSProperties,
    type ReactNode,
    Fragment,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Resizable,
    Button,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Callout,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon,
    FileIcon
} from "../../anatomy/icons";

const NAME = "Resizable";

const USAGE_CODE = `<Resizable.PanelGroup>
    <Resizable.Panel
        title="Sidebar"
        defaultSize={30}
        minSize={20}
    >
        <Sidebar/>
    </Resizable.Panel>
    <Resizable.Handle
        isGripVisible={true}
    />
    <Resizable.Panel
        title="Content"
    >
        <Content/>
    </Resizable.Panel>
</Resizable.PanelGroup>`;

const SIZES_CODE = `<Resizable.PanelGroup
    onLayoutChange={handleLayoutChange}
    persistKey="workspace"
>
    <Resizable.Panel
        defaultSize="240px"
        minSize="200px"
        maxSize="320px"
    >
        <Sidebar/>
    </Resizable.Panel>
    <Resizable.Handle/>
    <Resizable.Panel
        minSize={30}
    >
        <Content/>
    </Resizable.Panel>
</Resizable.PanelGroup>`;

const COLLAPSE_CODE = `const [isCollapsed, setIsCollapsed] = useState(false);

<Resizable.Panel
    onCollapsedChange={setIsCollapsed}
    isCollapsed={isCollapsed}
    isCollapsible={true}
    collapsedSize="56px"
    defaultSize="200px"
    minSize="160px"
>
    <Sidebar/>
</Resizable.Panel>`;

const COMPACT_CODE = `<Resizable.PanelGroup
    compactBehaviour="tabs"
    compactBreakpoint={600}
>
    <Resizable.Panel
        title="Editor"
        icon={FileIcon}
    >
        <Editor/>
    </Resizable.Panel>
    <Resizable.Handle/>
    <Resizable.Panel
        title="Preview"
        icon={SearchIcon}
    >
        <Preview/>
    </Resizable.Panel>
</Resizable.PanelGroup>`;

const COMPACT_BREAKPOINT = 100000;

const FRAME_STYLE: CSSProperties = {
    width: "100%",
    height: 200
};

const TALL_FRAME_STYLE: CSSProperties = {
    width: "100%",
    height: 260
};

const SHORT_FRAME_STYLE: CSSProperties = {
    width: "100%",
    height: 96
};

const CELL_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 8
};

const PANEL_STYLE = {
    padding: 16,
    gap: 4
};

const COLLAPSED_PANEL_STYLE = {
    padding: 8
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("resizablePage.orientationTitle"),
        id: "orientation"
    },
    {
        title: t("resizablePage.collapseTitle"),
        id: "collapse"
    },
    {
        title: t("resizablePage.handleTitle"),
        id: "handle"
    },
    {
        title: t("resizablePage.compactTitle"),
        id: "compact"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ResizablePage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        layout,
        setLayout
    ] = useState([
        40,
        60
    ]);

    const [
        isCollapsed,
        setIsCollapsed
    ] = useState(false);

    const handleToggleCollapsed = () => setIsCollapsed(!isCollapsed);

    const handleExpand = () => setIsCollapsed(false);

    const renderPanelContent = (title: string, body: string = t("sample.body")) => <Fragment>
        <Text
            variant="labelMediumSize"
            color="high"
        >
            {title}
        </Text>
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {body}
        </Text>
    </Fragment>;

    const renderCell = (label: string, node: ReactNode) => <div
        style={CELL_STYLE}
        key={label}
    >
        <Text
            variant="labelSmallSize"
            color="mid"
        >
            {label}
        </Text>
        {node}
    </div>;

    const renderPair = (handle: ReactNode) => <div
        style={SHORT_FRAME_STYLE}
    >
        <Resizable.PanelGroup>
            <Resizable.Panel
                style={PANEL_STYLE}
                minSize={20}
                maxSize={80}
            >
                {renderPanelContent(t("resizablePage.panelA"))}
            </Resizable.Panel>
            {handle}
            <Resizable.Panel
                style={PANEL_STYLE}
                minSize={20}
                maxSize={80}
            >
                {renderPanelContent(t("resizablePage.panelB"))}
            </Resizable.Panel>
        </Resizable.PanelGroup>
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("resizablePage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("resizablePage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("resizablePage.overviewCaption", [
                    layout.map((size) => `${Math.round(size)}%`).join(" / ")
                ])}
            >
                <div
                    style={FRAME_STYLE}
                >
                    <Resizable.PanelGroup
                        onLayoutChange={setLayout}
                    >
                        <Resizable.Panel
                            title={t("resizablePage.panelSidebar")}
                            style={PANEL_STYLE}
                            defaultSize={40}
                            minSize={20}
                            maxSize={70}
                        >
                            {renderPanelContent(t("resizablePage.panelSidebar"))}
                        </Resizable.Panel>
                        <Resizable.Handle
                            isGripVisible={true}
                        />
                        <Resizable.Panel
                            title={t("resizablePage.panelContent")}
                            style={PANEL_STYLE}
                            minSize={20}
                        >
                            {renderPanelContent(t("resizablePage.panelContent"))}
                        </Resizable.Panel>
                    </Resizable.PanelGroup>
                </div>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
            <Callout>
                {t("resizablePage.heightNote")}
            </Callout>
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("resizablePage.sizesText")}
            </Paragraph>
            <Preview>
                <div
                    style={FRAME_STYLE}
                >
                    <Resizable.PanelGroup>
                        <Resizable.Panel
                            title={t("resizablePage.panelSidebar")}
                            style={PANEL_STYLE}
                            defaultSize="160px"
                            minSize="120px"
                            maxSize="240px"
                        >
                            {renderPanelContent(t("resizablePage.panelSidebar"), "120px–240px")}
                        </Resizable.Panel>
                        <Resizable.Handle
                            isGripVisible={true}
                        />
                        <Resizable.Panel
                            title={t("resizablePage.panelContent")}
                            style={PANEL_STYLE}
                            minSize={30}
                        >
                            {renderPanelContent(t("resizablePage.panelContent"), "≥ 30%")}
                        </Resizable.Panel>
                        <Resizable.Handle
                            isGripVisible={true}
                        />
                        <Resizable.Panel
                            title={t("resizablePage.panelInspector")}
                            style={PANEL_STYLE}
                            defaultSize={25}
                            maxSize={40}
                            minSize={15}
                        >
                            {renderPanelContent(t("resizablePage.panelInspector"), "15%–40%")}
                        </Resizable.Panel>
                    </Resizable.PanelGroup>
                </div>
            </Preview>
            <Paragraph>
                {t("resizablePage.persistText")}
            </Paragraph>
            <CodeBlock
                code={SIZES_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("resizablePage.orientationTitle")}
            id="orientation"
        >
            <Paragraph>
                {t("resizablePage.orientationText")}
            </Paragraph>
            <Preview>
                <div
                    style={TALL_FRAME_STYLE}
                >
                    <Resizable.PanelGroup
                        orientation="vertical"
                    >
                        <Resizable.Panel
                            title={t("resizablePage.panelEditor")}
                            style={PANEL_STYLE}
                            defaultSize={65}
                            minSize={30}
                        >
                            {renderPanelContent(t("resizablePage.panelEditor"))}
                        </Resizable.Panel>
                        <Resizable.Handle
                            isGripVisible={true}
                        />
                        <Resizable.Panel
                            title={t("resizablePage.panelConsole")}
                            isCollapsible={true}
                            style={PANEL_STYLE}
                            defaultSize={35}
                            minSize={20}
                        >
                            {renderPanelContent(t("resizablePage.panelConsole"))}
                        </Resizable.Panel>
                    </Resizable.PanelGroup>
                </div>
            </Preview>
        </Section>
        <Section
            title={t("resizablePage.collapseTitle")}
            id="collapse"
        >
            <Paragraph>
                {t("resizablePage.collapseText")}
            </Paragraph>
            <Preview
                caption={t("resizablePage.collapseCaption", [
                    isCollapsed ? t("resizablePage.collapsed") : t("resizablePage.expanded")
                ])}
            >
                <div
                    style={FRAME_STYLE}
                >
                    <Resizable.PanelGroup>
                        <Resizable.Panel
                            style={isCollapsed ? COLLAPSED_PANEL_STYLE : PANEL_STYLE}
                            title={t("resizablePage.panelSidebar")}
                            onCollapsedChange={setIsCollapsed}
                            isCollapsed={isCollapsed}
                            isCollapsible={true}
                            collapsedSize="56px"
                            defaultSize="200px"
                            maxSize="280px"
                            minSize="160px"
                        >
                            {
                                isCollapsed ?
                                    <Button
                                        accessibilityLabel={t("resizablePage.panelSidebar")}
                                        onPress={handleExpand}
                                        variant="ghost"
                                        icon={FileIcon}
                                        type="neutral"
                                        size="small"
                                    />
                                :
                                    renderPanelContent(t("resizablePage.panelSidebar"))
                            }
                        </Resizable.Panel>
                        <Resizable.Handle
                            isGripVisible={true}
                        />
                        <Resizable.Panel
                            title={t("resizablePage.panelContent")}
                            style={PANEL_STYLE}
                            minSize="200px"
                        >
                            {renderPanelContent(t("resizablePage.panelContent"))}
                        </Resizable.Panel>
                    </Resizable.PanelGroup>
                </div>
                <Button
                    title={isCollapsed ? t("resizablePage.expand") : t("resizablePage.collapse")}
                    onPress={handleToggleCollapsed}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={COLLAPSE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("resizablePage.handleTitle")}
            id="handle"
        >
            <Paragraph>
                {t("resizablePage.handleText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {renderCell("<Resizable.Handle/>", renderPair(<Resizable.Handle/>))}
                {
                    renderCell("isGripVisible={true}", renderPair(<Resizable.Handle
                        isGripVisible={true}
                    />))
                }
                {
                    renderCell("isDisabled={true}", renderPair(<Resizable.Handle
                        isGripVisible={true}
                        isDisabled={true}
                    />))
                }
            </Preview>
        </Section>
        <Section
            title={t("resizablePage.compactTitle")}
            id="compact"
        >
            <Paragraph>
                {t("resizablePage.compactText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderCell("compactBehaviour=\"tabs\"", <Resizable.PanelGroup
                        compactBreakpoint={COMPACT_BREAKPOINT}
                        compactBehaviour="tabs"
                    >
                        <Resizable.Panel
                            title={t("resizablePage.panelEditor")}
                            style={PANEL_STYLE}
                            icon={FileIcon}
                        >
                            {renderPanelContent(t("resizablePage.panelEditor"))}
                        </Resizable.Panel>
                        <Resizable.Handle/>
                        <Resizable.Panel
                            title={t("resizablePage.panelPreview")}
                            style={PANEL_STYLE}
                            icon={SearchIcon}
                        >
                            {renderPanelContent(t("resizablePage.panelPreview"))}
                        </Resizable.Panel>
                    </Resizable.PanelGroup>)
                }
                {
                    renderCell("compactBehaviour=\"stack\"", <Resizable.PanelGroup
                        compactBreakpoint={COMPACT_BREAKPOINT}
                        compactBehaviour="stack"
                    >
                        <Resizable.Panel
                            title={t("resizablePage.panelEditor")}
                            style={PANEL_STYLE}
                        >
                            {renderPanelContent(t("resizablePage.panelEditor"))}
                        </Resizable.Panel>
                        <Resizable.Handle/>
                        <Resizable.Panel
                            title={t("resizablePage.panelPreview")}
                            style={PANEL_STYLE}
                        >
                            {renderPanelContent(t("resizablePage.panelPreview"))}
                        </Resizable.Panel>
                    </Resizable.PanelGroup>)
                }
            </Preview>
            <CodeBlock
                code={COMPACT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("resizablePage.a11yRole"),
                    t("resizablePage.a11yValues"),
                    t("resizablePage.a11yLabel"),
                    t("resizablePage.a11yKeyboard"),
                    t("resizablePage.a11yPointer"),
                    t("resizablePage.a11yDisabled"),
                    t("resizablePage.a11yCompact"),
                    t("resizablePage.a11yNative"),
                    t("resizablePage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <Paragraph>
                <InlineCode>
                    Resizable.PanelGroup
                </InlineCode>
                {" "}
                {t("resizablePage.partGroupText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("resizablePage.propOrientation"),
                        type: "\"horizontal\" | \"vertical\"",
                        defaultValue: "\"horizontal\"",
                        name: "orientation"
                    },
                    {
                        description: t("resizablePage.propIsFramed"),
                        defaultValue: "true",
                        name: "isFramed",
                        type: "boolean"
                    },
                    {
                        description: t("resizablePage.propOnLayoutChange"),
                        type: "(sizes: Array<number>) => void",
                        name: "onLayoutChange"
                    },
                    {
                        description: t("resizablePage.propPersistKey"),
                        type: "string | null",
                        defaultValue: "null",
                        name: "persistKey"
                    },
                    {
                        description: t("resizablePage.propCompactBreakpoint"),
                        defaultValue: "shapeConfigs.compactBreakpoint",
                        name: "compactBreakpoint",
                        type: "number"
                    },
                    {
                        description: t("resizablePage.propCompactBehaviour"),
                        type: "\"stack\" | \"tabs\"",
                        defaultValue: "\"stack\"",
                        name: "compactBehaviour"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("resizablePage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("resizablePage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("resizablePage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Resizable.Panel
                </InlineCode>
                {" "}
                {t("resizablePage.partPanelText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("resizablePage.propDefaultSize"),
                        type: "number | `${number}px`",
                        name: "defaultSize"
                    },
                    {
                        description: t("resizablePage.propMinSize"),
                        type: "number | `${number}px`",
                        defaultValue: "10",
                        name: "minSize"
                    },
                    {
                        description: t("resizablePage.propMaxSize"),
                        type: "number | `${number}px`",
                        defaultValue: "100",
                        name: "maxSize"
                    },
                    {
                        description: t("resizablePage.propIsCollapsible"),
                        defaultValue: "false",
                        name: "isCollapsible",
                        type: "boolean"
                    },
                    {
                        description: t("resizablePage.propCollapsedSize"),
                        type: "number | `${number}px`",
                        name: "collapsedSize"
                    },
                    {
                        description: t("resizablePage.propIsDefaultCollapsed"),
                        name: "isDefaultCollapsed",
                        type: "boolean"
                    },
                    {
                        description: t("resizablePage.propIsCollapsed"),
                        name: "isCollapsed",
                        type: "boolean"
                    },
                    {
                        description: t("resizablePage.propOnCollapsedChange"),
                        type: "(isCollapsed: boolean) => void",
                        name: "onCollapsedChange"
                    },
                    {
                        description: t("resizablePage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("resizablePage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("resizablePage.panelNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Resizable.Handle
                </InlineCode>
                {" "}
                {t("resizablePage.partHandleText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("resizablePage.propIsGripVisible"),
                        defaultValue: "false",
                        name: "isGripVisible",
                        type: "boolean"
                    },
                    {
                        description: t("resizablePage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("resizablePage.propKeyboardStep"),
                        name: "keyboardStep",
                        defaultValue: "5",
                        type: "number"
                    },
                    {
                        description: t("resizablePage.propHandleTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("resizablePage.propHandleCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("resizablePage.handleNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ResizablePage;
